import React, { useState, useEffect } from "react";
import { Button, Card, Space, Typography, Spin, Divider, Switch, message } from "antd";
import { SettingOutlined, TeamOutlined, BellOutlined, MailOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useKeycloak } from "@react-keycloak/web";
import { useTranslation } from "react-i18next";
import { getOrganizationSettings, saveOrganizationSettings } from "../redux/slieces/organizationSettingsSlice";
import { NotificationService } from "../services/notificationService";
import WorkerOrderModal from "./weekPlan/WorkerOrderModal";
import LicenseSection from "./LicenseSection";
import styles from "./OrganizationSettings.module.css";

const { Title, Text, Paragraph } = Typography;

const OrganizationSettings = () => {
  const { t } = useTranslation();
  const { keycloak, initialized } = useKeycloak();
  const dispatch = useDispatch();

  const [workerOrderModalVisible, setWorkerOrderModalVisible] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [notificationLoading, setNotificationLoading] = useState(false);

  const { settings, loading } = useSelector((state) => state.organizationSettings);

  // Check if user is authenticated and initialized
  if (!initialized) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" tip={t("loadingAuth")} />
      </div>
    );
  }

  // Check if user has admin role
  const isAdmin = keycloak.tokenParsed?.realm_access?.roles?.includes('mediadmin');

  // If not admin, show access denied
  if (!isAdmin) {
    return (
      <div className={styles.accessDenied}>
        <h2>{t("accessDenied")}</h2>
        <p>{t("adminAccessRequired")}</p>
        <p>{t("contactAdministrator")}</p>
      </div>
    );
  }

  // Fetch organization settings on mount
  useEffect(() => {
    dispatch(getOrganizationSettings());
  }, [dispatch]);

  // Fetch notification preference on mount
  useEffect(() => {
    const loadNotificationPreference = async () => {
      try {
        const pref = await NotificationService.getNotificationPreference();
        setNotificationsEnabled(pref.isEnabled);
      } catch (error) {
        // Default to enabled on error
      }
    };
    loadNotificationPreference();
  }, []);

  const handleNotificationToggle = async (checked) => {
    setNotificationLoading(true);
    try {
      await NotificationService.saveNotificationPreference(checked);
      setNotificationsEnabled(checked);
      message.success(t('notificationSaved'));
    } catch (error) {
      message.error(t('notificationSaveError'));
    } finally {
      setNotificationLoading(false);
    }
  };

  const openWorkerOrderModal = () => {
    setWorkerOrderModalVisible(true);
  };

  const closeWorkerOrderModal = () => {
    setWorkerOrderModalVisible(false);
  };

  const handleNotifyAdminOwnChangesToggle = async (checked) => {
    try {
      await dispatch(saveOrganizationSettings({
        ...settings,
        notifyAdminOwnChanges: checked
      })).unwrap();
      message.success(t('notifyAdminOwnChangesSaved'));
    } catch (error) {
      message.error(t('notifyAdminOwnChangesSaveError'));
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title level={2}>
          <SettingOutlined /> {t('organizationSettings')}
        </Title>
        <Paragraph type="secondary">
          {t('organizationSettingsDescription')}
        </Paragraph>
      </div>

      <Divider />

      <div className={styles.settingsSection}>
        <Card
          title={
            <Space>
              <TeamOutlined />
              <span>{t('workerOrderSettings')}</span>
            </Space>
          }
          className={styles.settingsCard}
        >
          <Paragraph>
            {t('workerOrderSettingsDescription')}
          </Paragraph>
          <Paragraph type="secondary">
            {settings?.customWorkerOrder
              ? t('customWorkerOrderActive')
              : t('customWorkerOrderNotSet')
            }
          </Paragraph>
          <Button
            type="primary"
            icon={<SettingOutlined />}
            onClick={openWorkerOrderModal}
            loading={loading}
          >
            {t('customizeWorkerOrder')}
          </Button>
        </Card>

        <Card
          data-testid="notification-settings-card"
          title={
            <Space>
              <BellOutlined />
              <span>{t('emailNotifications')}</span>
            </Space>
          }
          className={styles.settingsCard}
          style={{ marginTop: 16 }}
        >
          <Paragraph>
            {t('emailNotificationsDescription')}
          </Paragraph>
          <Space>
            <Switch
              data-testid="notification-toggle"
              checked={notificationsEnabled}
              onChange={handleNotificationToggle}
              loading={notificationLoading}
            />
            <Text data-testid="notification-status-text">
              {notificationsEnabled ? t('notificationEnabled') : t('notificationDisabled')}
            </Text>
          </Space>
        </Card>

        <Card
          title={
            <Space>
              <MailOutlined />
              <span>{t('notifyAdminOwnChanges')}</span>
            </Space>
          }
          className={styles.settingsCard}
          style={{ marginTop: 16 }}
        >
          <Paragraph>
            {t('notifyAdminOwnChangesDescription')}
          </Paragraph>
          <Space>
            <Switch
              checked={settings?.notifyAdminOwnChanges || false}
              onChange={handleNotifyAdminOwnChangesToggle}
              loading={loading}
            />
            <Text>
              {settings?.notifyAdminOwnChanges
                ? t('notifyAdminOwnChangesEnabled')
                : t('notifyAdminOwnChangesDisabled')
              }
            </Text>
          </Space>
        </Card>

        <LicenseSection />
      </div>

      <WorkerOrderModal
        visible={workerOrderModalVisible}
        onClose={closeWorkerOrderModal}
        isOrganizationWide={true}
      />
    </div>
  );
};

export default OrganizationSettings;
